/**
 * The demo commerce engine: from a tenant fixture and a clock, what happened on
 * each day — visitors through the funnel, entries sold per competition,
 * revenue, ad spend and attribution per channel.
 *
 * Every figure the Overview shows is a sum over these days, and the AI summary
 * and alerts read the same sums, so no two cards can disagree. Each day is a
 * pure function of (tenant seed, date, clock), so figures are stable across
 * screens, reloads and test runs.
 */

import { FUNNEL_STAGES, type FunnelStageKey } from '@/api/types'
import {
  addDays,
  dayCount,
  eachDay,
  toDateKey,
  type DateWindow,
  type GlobalFilters,
} from '@/lib/filters'

import { wobble } from '../random'
import type { CompetitionFixture, ShareFixture, TenantFixture } from '../tenants/types'

/** Share of a day's traffic in each hour, midnight first: quiet overnight, peaking in the evening. */
export const HOURLY_CURVE: readonly number[] = [
  1.2, 0.7, 0.4, 0.3, 0.3, 0.5, 1.2, 2.4, 3.4, 3.8, 4, 4.3, 5, 4.8, 4.4, 4.5, 4.9, 5.6, 6.6, 7.6,
  8.2, 7.4, 5.4, 3,
]
const CURVE_TOTAL = HOURLY_CURVE.reduce((sum, value) => sum + value, 0)

export function hourShare(hour: number): number {
  return (HOURLY_CURVE[hour] ?? 0) / CURVE_TOTAL
}

/** How much of a typical day's trade has happened by `now`. */
export function elapsedDayShare(now: Date): number {
  const hour = now.getHours()
  let share = 0
  for (let index = 0; index < hour; index += 1) share += HOURLY_CURVE[index] ?? 0
  share += (HOURLY_CURVE[hour] ?? 0) * (now.getMinutes() / 60)
  return share / CURVE_TOTAL
}

/** Entries for competitions the fixture does not name. */
export const ARCHIVED = '__archived'

export function round2(value: number): number {
  return Math.round(value * 100) / 100
}

// --- scope: what the global filters select ---------------------------------

/** The demo fixtures name no timezone: every demo tenant trades in the UK, as 676 does. */
export const DEMO_TIMEZONE = 'Europe/London'

export interface Scope {
  /** Traffic multiplier from environment, website, device and customer type. */
  traffic: number
  /** Further multiplier from channel, campaign and creative. */
  channelTraffic: number
  channelIds: readonly string[] | null
  competitionId: string | null
  customerType: string | null
  /** Ad spend is a production fact; a view with no production site has none. */
  includesProduction: boolean
}

function shareOf(list: readonly ShareFixture[], id: string | undefined): number {
  if (id === undefined) return 1
  return list.find((item) => item.id === id)?.share ?? 0
}

export function scopeFor(tenant: TenantFixture, filters: GlobalFilters): Scope {
  const { dimensions } = filters
  const environments = filters.environments.length > 0 ? filters.environments : ['production']
  const websites = tenant.websites.filter(
    (website) =>
      environments.includes(website.environment) &&
      (!dimensions.website || website.id === dimensions.website),
  )

  let traffic = websites.reduce((sum, website) => sum + website.trafficShare, 0)
  traffic *= shareOf(tenant.devices, dimensions.device)
  traffic *= shareOf(tenant.customerTypes, dimensions.customer_type)

  let channelTraffic = 1
  let channelIds: string[] | null = null
  const channelShare = (ids: readonly string[]) =>
    tenant.channels
      .filter((channel) => ids.includes(channel.id))
      .reduce((sum, channel) => sum + channel.trafficShare, 0)
  const restrict = (ids: readonly string[]) => {
    channelIds = channelIds === null ? [...ids] : channelIds.filter((id) => ids.includes(id))
  }

  if (dimensions.channel) {
    restrict([dimensions.channel])
    channelTraffic *= channelShare([dimensions.channel])
  }
  if (dimensions.campaign) {
    const ids = tenant.campaigns.find((campaign) => campaign.id === dimensions.campaign)?.channels ?? []
    restrict(ids)
    channelTraffic *= channelShare(ids) * 0.5
  }
  if (dimensions.creative) {
    const creative = tenant.creatives.find((item) => item.id === dimensions.creative)
    const ids = creative ? [creative.channel] : []
    restrict(ids)
    channelTraffic *= channelShare(ids) * 0.3
  }

  return {
    traffic,
    channelTraffic,
    channelIds,
    competitionId: dimensions.competition ?? null,
    customerType: dimensions.customer_type ?? null,
    includesProduction: websites.some((website) => website.environment === 'production'),
  }
}

// --- totals ------------------------------------------------------------------

export interface CompetitionTotals {
  tickets: number
  revenue: number
  views: number
  orders: number
}

export interface ChannelTotals {
  spend: number
  attributedRevenue: number
  orders: number
}

export interface Totals {
  stages: Record<FunnelStageKey, number>
  orders: number
  tickets: number
  revenue: number
  newCustomers: number
  returningCustomers: number
  adSpend: number
  attributedRevenue: number
}

export interface DayTotals extends Totals {
  date: string
  competitions: Map<string, CompetitionTotals>
  channels: Map<string, ChannelTotals>
}

export interface PeriodTotals extends Totals {
  window: DateWindow
  competitions: Map<string, CompetitionTotals>
  channels: Map<string, ChannelTotals>
  days: DayTotals[]
}

interface RawDay {
  stages: Record<FunnelStageKey, number>
  tickets: number
  fraction: number
}

export interface CompetitionStatus {
  opens: string
  closes: string
  soldToDate: number
  sellThrough: number
  expected: number
  velocityPerDay: number
  pacing: 'ahead' | 'on_track' | 'behind'
  daysLeft: number
}

function emptyStages(): Record<FunnelStageKey, number> {
  return Object.fromEntries(FUNNEL_STAGES.map((stage) => [stage, 0])) as Record<
    FunnelStageKey,
    number
  >
}

/**
 * Splits whole tickets across competitions by weight (largest remainder).
 * `unlistedWeight` is demand for competitions the fixture does not name; it
 * always takes its share, so a quiet week with few named competitions does not
 * pour every entry into them.
 */
export function allocate(
  total: number,
  competitions: readonly CompetitionFixture[],
  unlistedWeight = 0,
): Map<string, number> {
  const entries = [
    ...competitions.map((competition) => ({ id: competition.id, weight: competition.weight })),
    ...(unlistedWeight > 0 || competitions.length === 0 ? [{ id: ARCHIVED, weight: unlistedWeight || 1 }] : []),
  ]
  const weightTotal = entries.reduce((sum, entry) => sum + entry.weight, 0)
  const exact = entries.map((entry) => ({
    id: entry.id,
    value: (total * entry.weight) / weightTotal,
  }))
  const result = new Map(exact.map((entry) => [entry.id, Math.floor(entry.value)]))
  let remainder = total - [...result.values()].reduce((sum, value) => sum + value, 0)
  const byFraction = [...exact].sort(
    (a, b) => b.value - Math.floor(b.value) - (a.value - Math.floor(a.value)) || a.id.localeCompare(b.id),
  )
  for (const entry of byFraction) {
    if (remainder <= 0) break
    result.set(entry.id, (result.get(entry.id) ?? 0) + 1)
    remainder -= 1
  }
  return result
}

export class DemoDataset {
  readonly today: string
  private readonly raw = new Map<string, RawDay>()
  private readonly dayCache = new Map<string, DayTotals>()
  private inventory: Map<string, Map<string, number>> | null = null
  private readonly archivedPrice: number

  constructor(
    readonly tenant: TenantFixture,
    readonly now: Date,
  ) {
    this.today = toDateKey(now)
    this.archivedPrice =
      tenant.competitions.reduce((sum, competition) => sum + competition.ticketPrice, 0) /
      Math.max(1, tenant.competitions.length)
  }

  schedule(competition: CompetitionFixture): { opens: string; closes: string } {
    const opens = addDays(this.today, competition.opensInDays)
    return { opens, closes: addDays(opens, competition.durationDays - 1) }
  }

  /** Open by schedule, sold out or not. */
  openOn(date: string): CompetitionFixture[] {
    return this.tenant.competitions.filter((competition) => {
      const { opens, closes } = this.schedule(competition)
      return opens <= date && date <= closes
    })
  }

  /** Open, and not sold out at the start of the day. */
  onSaleOn(date: string): CompetitionFixture[] {
    const remaining = this.remainingOn(date)
    return this.openOn(date).filter(
      (competition) => (remaining.get(competition.id) ?? competition.maxEntries) > 0,
    )
  }

  /** Entries left at the start of `date`, per open competition. */
  private remainingOn(date: string): Map<string, number> {
    this.inventory ??= this.buildInventory()
    return this.inventory.get(date) ?? new Map()
  }

  /**
   * Walks every day from the first competition opening to today, selling
   * entries up to each competition's maximum, so a competition that sells out
   * stops taking entries — and stops being on sale — the day it does.
   */
  private buildInventory(): Map<string, Map<string, number>> {
    const inventory = new Map<string, Map<string, number>>()
    const first = this.tenant.competitions
      .map((competition) => this.schedule(competition).opens)
      .filter((opens) => opens <= this.today)
      .sort()[0]
    if (!first) return inventory

    const sold = new Map<string, number>()
    for (const date of eachDay({ from: first, to: this.today, days: dayCount(first, this.today) })) {
      const open = this.openOn(date)
      const remaining = new Map(
        open.map((competition) => [competition.id, competition.maxEntries - (sold.get(competition.id) ?? 0)]),
      )
      inventory.set(date, remaining)
      const onSale = open.filter((competition) => (remaining.get(competition.id) ?? 0) > 0)
      const allocation = allocate(
        Math.round(this.rawDay(date).tickets),
        onSale,
        this.tenant.commerce.unlistedDemandWeight,
      )
      for (const competition of onSale) {
        const sale = Math.min(allocation.get(competition.id) ?? 0, remaining.get(competition.id) ?? 0)
        sold.set(competition.id, (sold.get(competition.id) ?? 0) + sale)
      }
    }
    return inventory
  }

  activeCount(date: string, scope: Scope): number {
    const on = date > this.today ? this.today : date
    return this.onSaleOn(on).filter(
      (competition) => !scope.competitionId || competition.id === scope.competitionId,
    ).length
  }

  /** A day before any filter: the whole tenant, production traffic. */
  private rawDay(date: string, fractionOverride?: number): RawDay {
    const key = `${date}|${fractionOverride ?? ''}`
    const cached = this.raw.get(key)
    if (cached) return cached

    const { tenant } = this
    const { seed } = tenant
    const daysAgo = dayCount(date, this.today) - 1
    const fraction =
      fractionOverride ?? (daysAgo < 0 ? 0 : daysAgo === 0 ? elapsedDayShare(this.now) : 1)
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay()
    const growth = Math.max(0.4, 1 - tenant.traffic.dailyGrowth * Math.max(0, daysAgo))
    const recent = daysAgo >= 0 && daysAgo < tenant.anomalies.recentDays

    const stages = emptyStages()
    stages.visitors =
      tenant.traffic.dailyVisitors *
      (tenant.traffic.weekdayFactors[weekday] ?? 1) *
      growth *
      wobble(seed, 0.1, 'visitors', date) *
      fraction

    for (let index = 1; index < FUNNEL_STAGES.length; index += 1) {
      const stage = FUNNEL_STAGES[index] as Exclude<FunnelStageKey, 'visitors'>
      const previous = FUNNEL_STAGES[index - 1] as FunnelStageKey
      let rate = tenant.funnelRates[stage] * wobble(seed, 0.035, stage, date)
      if (recent && stage === 'payment_attempted') rate *= tenant.anomalies.checkoutConversion
      if (recent && stage === 'purchase_completed') {
        rate = (1 - tenant.anomalies.paymentFailureRate) * wobble(seed, 0.01, stage, date)
      }
      stages[stage] = stages[previous] * Math.min(rate, 1)
    }

    const day: RawDay = {
      stages,
      tickets:
        stages.purchase_completed * tenant.commerce.ticketsPerOrder * wobble(seed, 0.06, 'tickets', date),
      fraction,
    }
    this.raw.set(key, day)
    return day
  }

  /**
   * One day under a traffic multiplier and optional competition and
   * customer-type focus. Channel totals are always computed from the day
   * *before* the channel multiplier, so a channel's attributed share is of the
   * whole, not of itself.
   */
  day(date: string, scope: Scope, fractionOverride?: number): DayTotals {
    const key = [
      date,
      scope.traffic,
      scope.channelTraffic,
      scope.channelIds?.join(',') ?? '*',
      scope.competitionId ?? '*',
      scope.customerType ?? '*',
      scope.includesProduction,
      fractionOverride ?? '',
    ].join('|')
    const cached = this.dayCache.get(key)
    if (cached) return cached

    const base = this.focusedDay(date, scope, scope.traffic, fractionOverride)
    const scoped =
      scope.channelTraffic === 1
        ? base
        : this.focusedDay(date, scope, scope.traffic * scope.channelTraffic, fractionOverride)
    const channels = this.channelsFor(date, base, scope, fractionOverride)

    const day: DayTotals = {
      ...scoped,
      channels,
      adSpend: round2([...channels.values()].reduce((sum, channel) => sum + channel.spend, 0)),
      attributedRevenue: round2(
        [...channels.values()].reduce((sum, channel) => sum + channel.attributedRevenue, 0),
      ),
    }
    this.dayCache.set(key, day)
    return day
  }

  private focusedDay(
    date: string,
    scope: Scope,
    factor: number,
    fractionOverride?: number,
  ): Omit<DayTotals, 'channels' | 'adSpend' | 'attributedRevenue'> {
    const { tenant } = this
    const raw = this.rawDay(date, fractionOverride)
    const active = this.onSaleOn(date)
    const remaining = this.remainingOn(date)
    const unlisted = active.length === 0 ? 1 : tenant.commerce.unlistedDemandWeight
    const weightTotal = active.reduce((sum, competition) => sum + competition.weight, 0) + unlisted
    const allTickets = Math.round(raw.tickets * factor)
    const allocation = allocate(allTickets, active, unlisted)

    const competitions = new Map<string, CompetitionTotals>()
    for (const [id, allocated] of allocation) {
      const fixture = active.find((competition) => competition.id === id)
      const share = (fixture ? fixture.weight : unlisted) / weightTotal
      // A competition cannot sell past its maximum; demand beyond it is lost.
      const tickets = fixture
        ? Math.min(allocated, Math.round((remaining.get(id) ?? fixture.maxEntries) * factor))
        : allocated
      competitions.set(id, {
        tickets,
        revenue: round2(tickets * (fixture?.ticketPrice ?? this.archivedPrice)),
        views: raw.stages.competition_viewed * factor * share,
        orders: raw.stages.purchase_completed * factor * share,
      })
    }

    let focus = 1
    if (scope.competitionId) {
      const fixture = active.find((competition) => competition.id === scope.competitionId)
      focus = fixture ? fixture.weight / weightTotal : 0
      for (const id of [...competitions.keys()]) if (id !== scope.competitionId) competitions.delete(id)
    }

    const stages = emptyStages()
    for (const stage of FUNNEL_STAGES) stages[stage] = Math.round(raw.stages[stage] * factor * focus)

    const tickets = [...competitions.values()].reduce((sum, competition) => sum + competition.tickets, 0)
    const revenue = round2([...competitions.values()].reduce((sum, competition) => sum + competition.revenue, 0))
    const orders = stages.purchase_completed
    const customers = Math.round(orders * tenant.commerce.customersPerOrder)
    const newShare = Math.min(0.95, tenant.commerce.newCustomerShare * wobble(tenant.seed, 0.1, 'new', date))
    const newCustomers =
      scope.customerType === 'returning'
        ? 0
        : scope.customerType === 'new'
          ? customers
          : Math.round(customers * newShare)

    return {
      date,
      stages,
      orders,
      tickets,
      revenue,
      newCustomers,
      returningCustomers: customers - newCustomers,
      competitions,
    }
  }

  private channelsFor(
    date: string,
    base: Omit<DayTotals, 'channels' | 'adSpend' | 'attributedRevenue'>,
    scope: Scope,
    fractionOverride?: number,
  ): Map<string, ChannelTotals> {
    const { tenant } = this
    const raw = this.rawDay(date, fractionOverride)
    const weights = tenant.channels.map(
      (channel) => channel.attributionWeight * wobble(tenant.seed, 0.15, 'attribution', channel.id, date),
    )
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0)
    const attributed = base.revenue * tenant.commerce.attributedShare
    const attributedOrders = base.orders * tenant.commerce.attributedShare

    const channels = new Map<string, ChannelTotals>()
    tenant.channels.forEach((channel, index) => {
      if (scope.channelIds && !scope.channelIds.includes(channel.id)) return
      const share = (weights[index] ?? 0) / weightTotal
      channels.set(channel.id, {
        spend: scope.includesProduction
          ? round2(channel.dailySpend * wobble(tenant.seed, 0.12, 'spend', channel.id, date) * raw.fraction)
          : 0,
        attributedRevenue: round2(attributed * share),
        orders: attributedOrders * share,
      })
    })
    return channels
  }

  /**
   * Every day in the window, summed. `partialLastDay` aligns a comparison
   * period with a current period that ends part-way through today, so "today
   * so far" is compared with "yesterday by this time", not all of yesterday.
   */
  period(window: DateWindow, scope: Scope, partialLastDay?: number): PeriodTotals {
    const totals: PeriodTotals = {
      window,
      stages: emptyStages(),
      orders: 0,
      tickets: 0,
      revenue: 0,
      newCustomers: 0,
      returningCustomers: 0,
      adSpend: 0,
      attributedRevenue: 0,
      competitions: new Map(),
      channels: new Map(),
      days: [],
    }

    for (const date of eachDay(window)) {
      const day = this.day(date, scope, date === window.to ? partialLastDay : undefined)
      totals.days.push(day)
      for (const stage of FUNNEL_STAGES) totals.stages[stage] += day.stages[stage]
      totals.orders += day.orders
      totals.tickets += day.tickets
      totals.revenue += day.revenue
      totals.newCustomers += day.newCustomers
      totals.returningCustomers += day.returningCustomers
      totals.adSpend += day.adSpend
      totals.attributedRevenue += day.attributedRevenue
      for (const [id, competition] of day.competitions) {
        const sum = totals.competitions.get(id) ?? { tickets: 0, revenue: 0, views: 0, orders: 0 }
        totals.competitions.set(id, {
          tickets: sum.tickets + competition.tickets,
          revenue: sum.revenue + competition.revenue,
          views: sum.views + competition.views,
          orders: sum.orders + competition.orders,
        })
      }
      for (const [id, channel] of day.channels) {
        const sum = totals.channels.get(id) ?? { spend: 0, attributedRevenue: 0, orders: 0 }
        totals.channels.set(id, {
          spend: sum.spend + channel.spend,
          attributedRevenue: sum.attributedRevenue + channel.attributedRevenue,
          orders: sum.orders + channel.orders,
        })
      }
    }

    totals.revenue = round2(totals.revenue)
    totals.adSpend = round2(totals.adSpend)
    totals.attributedRevenue = round2(totals.attributedRevenue)
    for (const competition of totals.competitions.values()) {
      competition.revenue = round2(competition.revenue)
      competition.orders = Math.round(competition.orders)
    }
    for (const channel of totals.channels.values()) {
      channel.spend = round2(channel.spend)
      channel.attributedRevenue = round2(channel.attributedRevenue)
      channel.orders = Math.round(channel.orders)
    }
    return totals
  }

  /** Sell-through and pacing, for the whole tenant regardless of filters. */
  competitionStatus(competition: CompetitionFixture): CompetitionStatus {
    const { opens, closes } = this.schedule(competition)
    const production = scopeFor(this.tenant, {
      range: '30d',
      from: null,
      to: null,
      compare: 'none',
      environments: [],
      dimensions: {},
    })
    const lastSaleDay = closes < this.today ? closes : this.today
    const soldDays = opens <= lastSaleDay ? eachDay({ from: opens, to: lastSaleDay, days: dayCount(opens, lastSaleDay) }) : []
    const soldByDay = soldDays.map((date) => this.day(date, production).competitions.get(competition.id)?.tickets ?? 0)
    const soldToDate = Math.min(
      competition.maxEntries,
      soldByDay.reduce((sum, tickets) => sum + tickets, 0),
    )

    const recent = soldByDay.slice(-7)
    const velocityPerDay = recent.length ? recent.reduce((sum, tickets) => sum + tickets, 0) / recent.length : 0
    const sellThrough = soldToDate / competition.maxEntries
    const elapsedDays = soldDays.length === 0 ? 0 : soldDays.length - (lastSaleDay === this.today ? 1 - elapsedDayShare(this.now) : 0)
    const expected = Math.min(1, elapsedDays / competition.durationDays)
    const ratio = expected === 0 ? 1 : sellThrough / expected
    const pacing = ratio >= 1.1 ? 'ahead' : ratio < 0.85 ? 'behind' : 'on_track'

    return {
      opens,
      closes,
      soldToDate,
      sellThrough,
      expected,
      velocityPerDay,
      pacing,
      daysLeft: closes < this.today ? 0 : dayCount(this.today, closes) - 1,
    }
  }
}
